/** A11: what each invoice/receipt range has used, and what is left to surrender. */
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { shortDate } from '../lib/format';
import { Card, Skeleton, StatusChip, TableWrap, Td, Th } from './ui';

interface Usage { id: string; branch: string; docType: string; mode: string; status: string; range: string; issued: number; firstIssued: string | null; lastIssued: string | null; firstDate: string | null; lastDate: string | null; cancelled: string[]; unused: { from: string; to: string; count: number } | null; closedAt: string | null; closedReason: string | null }

export function SeriesUsage(): JSX.Element {
  const q = useQuery({ queryKey: ['series-usage'], queryFn: () => api<Usage[]>('/company/document-series/usage') });
  return (
    <Card title="Series usage & surrender report" description="For each registered range: what was issued, what was cancelled, and the unused numbers to report to the RDO when a range is closed." className="mt-4">
      {q.isLoading ? <Skeleton className="h-24 w-full" /> : (
        <TableWrap>
          <thead><tr><Th>Branch · document</Th><Th>Range</Th><Th>Issued</Th><Th>Cancelled</Th><Th>Unused</Th><Th>Status</Th></tr></thead>
          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {q.data?.map((u) => (
              <tr key={u.id}>
                <Td>{u.branch}<span className="block text-xs text-slate-500">{u.docType.replace(/_/g, ' ').toLowerCase()} · {u.mode.toLowerCase()}</span></Td>
                <Td className="font-mono text-xs">{u.range}</Td>
                <Td>{u.issued ? <>{u.issued} <span className="block font-mono text-xs text-slate-500">{u.firstIssued} – {u.lastIssued}</span><span className="block text-xs text-slate-500">{shortDate(u.firstDate)} – {shortDate(u.lastDate)}</span></> : '—'}</Td>
                <Td className="font-mono text-xs">{u.cancelled.length ? u.cancelled.join(', ') : '—'}</Td>
                <Td>{u.unused ? <><span className="tabular-nums">{u.unused.count.toLocaleString()}</span><span className="block font-mono text-xs text-slate-500">{u.unused.from} – {u.unused.to}</span></> : 'None'}</Td>
                <Td><StatusChip tone={u.status === 'ACTIVE' ? 'ok' : 'neutral'}>{u.status.toLowerCase()}</StatusChip>{u.closedReason && <span className="block text-xs text-slate-500">{shortDate(u.closedAt)}: {u.closedReason}</span>}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Card>
  );
}
